import type { CommandArgument, Reading, ThermostatState } from '@casa/driver-sdk';
import type { GaugeStyle, TileOf } from '@casa/model';
import type { IconDefinition } from '../../elements/icon.ts';
import { lastNumber, lastValue } from '../../values/readings.ts';
import { DEFAULT_VALUE_COLOR, formatTileNumber, gaugeLookOf, type ValueTileContent } from '../value/value_tile.ts';

/**
 * The legacy `thermostat` tile (`ThermostatTile.cs`): a gauge (10 to 30, blue-yellow-red by
 * default) with the temperature in the middle and the set point below, drawn as a value tile's
 * gauge (`valueTileElements`). A tap opens the set-point picker (`setpoint_picker.tsx`).
 *
 * Set up one of two ways (device-roles.md TH-1): a thermostat, whose one source is read whole as the
 * thermostat role (its temperature, set point, mode and state, DR-5), taking `set_setpoint` and
 * `set_mode`; or separate values, a source each, the set point's command chosen (`set_setpoint`
 * when left out) and perhaps a mode. An icon shows the mode and whether it heats or cools (TH-2).
 *
 * The legacy tile meant 10 to 30, but read `min` and `max` from the absent `gauge` option, which
 * made both 0, so it never drew the value arc. This one draws it.
 */

export type ThermostatTile = TileOf<'thermostat'>;

type ThermostatOptions = ThermostatTile['options'];
type ByValues = Extract<ThermostatOptions, { temperature: unknown }>;

/** The tile's options set up from separate values (TH-1), or undefined for a thermostat. */
export function byValuesOf(options: ThermostatOptions) : ByValues | undefined {
    return 'temperature' in options ? options : undefined;
}

/** The sources of the temperature, the set point and the mode: a thermostat's one for all three. */
export function sourceNamesOf(options: ThermostatOptions) : [temperature: string, setpoint: string, mode: string | undefined] {
    return 'temperature' in options ? [options.temperature.source, options.setpoint.source, options.mode?.source] : [options.source, options.source, options.source];
}

/** The number formats of the temperature and the set point: a thermostat's one for both. */
export function numberFormatsOf(options: ThermostatOptions) : { temperature: string; setpoint: string } {
    return 'temperature' in options ? { temperature: options.temperature.numberFormat, setpoint: options.setpoint.numberFormat } : { temperature: options.numberFormat, setpoint: options.numberFormat };
}

export interface ThermostatTileData {
    temperature: number | null;
    setpoint: number | null;
    /** The mode as the device names it (`heat`), where it has one. */
    mode?: string | undefined;
    state?: ThermostatState | undefined;
}

const isRecord = (value: unknown) : value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const numberOrNull = (value: unknown) : number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);
const STATES: ReadonlySet<string> = new Set(['heating', 'cooling', 'idle', 'off']);

/** What a thermostat's reading says, read whole (DR-5); nothing known for anything else. */
export function thermostatData(readings: Reading[] | null | undefined) : ThermostatTileData {
    const record = lastValue(readings);
    if (!isRecord(record)) {
        return { temperature: null, setpoint: null };
    }

    return {
        temperature: numberOrNull(record.temperature),
        setpoint: numberOrNull(record.setpoint),
        ...(typeof record.mode === 'string' && record.mode !== '' ? { mode: record.mode } : {}),
        ...(typeof record.state === 'string' && STATES.has(record.state) ? { state: record.state as ThermostatState } : {}),
    };
}

/** What separate values say: a number each, and the mode as a text. */
export function separateData(temperature: Reading[] | null | undefined, setpoint: Reading[] | null | undefined, mode: Reading[] | null | undefined) : ThermostatTileData {
    const modeValue = lastValue(mode);

    return {
        temperature: lastNumber(temperature),
        setpoint: lastNumber(setpoint),
        ...(typeof modeValue === 'string' && modeValue !== '' ? { mode: modeValue } : {}),
    };
}

/** A mode in words, for the card where its command does not name it: as the device does, capitalised. */
export function modeLabel(mode: string) : string {
    return `${mode.slice(0, 1).toUpperCase()}${mode.slice(1)}`;
}

const FLAME = 'fire';
const SNOWFLAKE = 'snowflake';
const HEATING_COLOR = '#ff8a3d';
const COOLING_COLOR = '#4fb3ff';
const RESTING_COLOR = '#808080';
const HEATING_MODES: ReadonlySet<string> = new Set(['heat', 'emergency heat']);

/**
 * The icon for the mode and the state (TH-2, the owner): an orange flame while it heats and a blue
 * snowflake while it cools, whatever the mode; otherwise, grey, the mode's own (a flame in heat
 * mode, a snowflake in cool mode), and nothing in another (auto, off).
 */
export function stateIconOf(mode: string | undefined, state: ThermostatState | undefined) : { name: string; color: string } | undefined {
    if (state === 'heating') {
        return { name: FLAME, color: HEATING_COLOR };
    }
    if (state === 'cooling') {
        return { name: SNOWFLAKE, color: COOLING_COLOR };
    }
    if (mode !== undefined && HEATING_MODES.has(mode)) {
        return { name: FLAME, color: RESTING_COLOR };
    }
    if (mode === 'cool') {
        return { name: SNOWFLAKE, color: RESTING_COLOR };
    }

    return undefined;
}

/**
 * What a source shows at once after a command (device-roles.md TH-4): a thermostat's record with the
 * set point or mode changed, else the value alone. A new mode forgets what the thermostat was doing
 * (`state`) until it says: else a flame of heating would stay over a mode of cooling until the next
 * reading (the Mac's thermostat tile results, note 1).
 */
export function sentReadings(readings: Reading[] | null | undefined, isRole: boolean, key: 'setpoint' | 'mode', value: unknown) : Reading[] {
    const record = lastValue(readings);
    if (!isRole || !isRecord(record)) {
        return [{ value }];
    }

    const { state: _state, ...rest } = record;

    return [{ value: key === 'mode' ? { ...rest, mode: value } : { ...record, [key]: value } }];
}

export interface ThermostatLook {
    /** The state's icon, loaded (`stateIconOf`). */
    stateIcon?: IconDefinition | undefined;
}

export function thermostatTileContent(tile: ThermostatTile, data: ThermostatTileData, locale: string | undefined, textColor = DEFAULT_VALUE_COLOR, gaugeStyle: GaugeStyle = 'ticks', look: ThermostatLook = {}) : ValueTileContent {
    const { gauge } = tile.options;
    const byValues = byValuesOf(tile.options);
    const formats = numberFormatsOf(tile.options);
    // A thermostat's set point with a degree sign; separate values with the unit chosen, none by default.
    const unit = byValues ? byValues.setpoint.unit : '°';
    const stateColor = stateIconOf(data.mode, data.state)?.color;

    return {
        numberText: formatTileNumber(data.temperature, formats.temperature, locale),
        unitText: '',
        unitScale: 1,
        color: textColor,
        gauge: {
            value: data.temperature,
            min: gauge.min,
            max: gauge.max,
            ...(look.stateIcon && stateColor ? { icon: look.stateIcon } : {}),
            iconColor: stateColor ?? textColor,
            secondaryText: `${formatTileNumber(data.setpoint, formats.setpoint, locale)}${unit}`,
            secondaryColor: textColor,
            // The set point is the second value, on the scale where the style shows one (QG-4).
            style: gaugeStyle,
            secondaryValue: data.setpoint,
            marksSecond: gauge.look?.markSecond ?? true,
            locale,
            ...gaugeLookOf(gauge),
        },
    };
}

/** The set points the picker offers: from `min` to `max` in steps of `step`. */
export interface SetpointRange {
    readonly min: number;
    readonly max: number;
    readonly step: number;
}

/** What the legacy picker offered (`ThermostatTile.Tap`): 15 to 30 in steps of 0.5 (TP-3). */
export const DEFAULT_SETPOINT_RANGE: SetpointRange = { min: 15, max: 30, step: 0.5 };

/** The same in Fahrenheit, in whole degrees (mine). */
const FAHRENHEIT_RANGE: SetpointRange = { min: 59, max: 86, step: 1 };

/**
 * The picker's range from the set-point command's argument (TH-3): its `min`, `max` and `step`,
 * each where it gives one; the legacy range otherwise, or its Fahrenheit counterpart for °F.
 */
export function setpointRange(argument: CommandArgument | undefined) : SetpointRange {
    const fallback = argument?.unit === '°F' ? FAHRENHEIT_RANGE : DEFAULT_SETPOINT_RANGE;
    const min = argument?.min ?? fallback.min;
    const max = argument?.max ?? fallback.max;
    const step = argument?.step !== undefined && argument.step > 0 ? argument.step : fallback.step;

    return max > min ? { min, max, step } : fallback;
}

/** `value` moved by `steps` steps, on a step and within the range. */
export function stepSetpoint(value: number, steps: number, range: SetpointRange = DEFAULT_SETPOINT_RANGE) : number {
    // Rounded to hundredths, so that steps of 0.1 do not add up to 21.299999.
    const stepped = Math.round((Math.round(value / range.step + steps) * range.step) * 100) / 100;

    return Math.min(range.max, Math.max(range.min, stepped));
}

/** Where the picker starts: the set point on its nearest step; without one the temperature's; else 20. */
export function startingSetpoint(setpoint: number | null, temperature: number | null, range: SetpointRange = DEFAULT_SETPOINT_RANGE) : number {
    return stepSetpoint(setpoint ?? temperature ?? 20, 0, range);
}

/**
 * The picker's quick choices: five whole degrees around `start`, kept within the range (fewer where
 * it is narrower). They do not follow the − and + buttons, so that they stay put under a finger.
 */
export function quickSetpoints(start: number, range: SetpointRange = DEFAULT_SETPOINT_RANGE) : number[] {
    const lowest = Math.ceil(range.min);
    const highest = Math.floor(range.max);
    const count = Math.max(0, Math.min(5, highest - lowest + 1));
    const first = Math.max(lowest, Math.min(Math.round(start) - 2, highest - count + 1));

    return Array.from({ length: count }, (_, index) => first + index);
}
